import { useLayoutEffect, useRef } from 'react';
import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';

export type MobileHeaderProps = Omit<React.ComponentProps<'div'>, 'style' | 'ref'>;

/*
 * The mobile topbar: logo left, hamburger right. Invisible above the breakpoint.
 * Its height is published as `--app-mobile-header-h`, so a page's own header
 * can stick right under it (`sticky-below-header`).
 */
export function MobileHeader({ className, ...props }: MobileHeaderProps) {
  const header = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = header.current;
    if (!element) return;

    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty('--app-mobile-header-h', `${element.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(element);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--app-mobile-header-h');
    };
  }, []);

  return (
    <div
      ref={header}
      data-slot='mobile-header'
      className={cn(
        /* Sits under the impersonation banner when there is one; `--app-banner-h` is 0 otherwise. */
        'hidden max-mobile:flex sticky top-[var(--app-banner-h,0px)] z-20 items-center justify-between',
        'border-b border-line bg-canvas/92 px-4.5 pt-[max(0.875rem,env(safe-area-inset-top))] pb-3.5 backdrop-blur-sm',
        className,
      )}
      {...props}
    />
  );
}

export interface MobileNavProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  open: boolean;
}

/*
 * The slide-in mobile navigation.
 *
 * Above the breakpoint this is `display:none`, never merely parked off-screen
 * with a transform — a transformed element is still in the DOM, still
 * focusable, and still read by a screen reader. Two implementations of this
 * panel diverged once before; the visibility rule is what went wrong.
 *
 * Inside the breakpoint the panel is laid out and the transform animates it in,
 * which is why the translate classes carry the max-mobile: prefix too.
 */
export function MobileNav({ open, className, ...props }: MobileNavProps) {
  return (
    <div
      data-slot='mobile-nav'
      /* aria-hidden matches the visual state so the closed panel is not reachable. */
      aria-hidden={!open}
      className={cn(
        'hidden max-mobile:flex fixed inset-0 z-30 flex-col bg-canvas',
        'transition-transform duration-300 ease-out',
        open ? 'translate-x-0' : 'translate-x-full',
        className,
      )}
      {...props}
    />
  );
}

export type MobileNavHeaderProps = Omit<React.ComponentProps<'div'>, 'style'>;

export function MobileNavHeader({ className, ...props }: MobileNavHeaderProps) {
  return (
    <div
      data-slot='mobile-nav-header'
      className={cn(
        'flex shrink-0 items-center justify-between border-b border-line px-4.5 pt-[max(0.875rem,env(safe-area-inset-top))] pb-3.5',
        className,
      )}
      {...props}
    />
  );
}

export type MobileNavListProps = Omit<React.ComponentProps<'div'>, 'style'>;

/* Mirrors the rail: same items, same order, same active item. */
export function MobileNavList({ className, ...props }: MobileNavListProps) {
  return (
    <div
      data-slot='mobile-nav-list'
      className={cn('flex-1 overflow-y-auto px-2 pt-2 pb-6', className)}
      {...props}
    />
  );
}

export type MobileNavFooterProps = Omit<React.ComponentProps<'div'>, 'style'>;

/* Whatever lives in the desktop account menu belongs here, not in the list. */
export function MobileNavFooter({ className, ...props }: MobileNavFooterProps) {
  return (
    <div
      data-slot='mobile-nav-footer'
      className={cn(
        'shrink-0 px-2 pb-[max(1.5rem,env(safe-area-inset-bottom))]',
        className,
      )}
      {...props}
    />
  );
}

export const mobileNavItemVariants = cva(
  [
    'block w-full rounded-md px-3.5 py-3.75 text-left text-lg font-semibold transition-colors',
    'aria-disabled:opacity-40 aria-disabled:hover:bg-transparent',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
  ],
  {
    variants: {
      tone: {
        default: 'text-ink hover:bg-fill',
        danger: 'text-danger hover:bg-danger-fill',
      },
      active: {
        true: 'bg-accent/15',
        false: '',
      },
    },
    defaultVariants: {
      tone: 'default',
      active: false,
    },
  },
);

export type MobileNavItemVariants = VariantProps<typeof mobileNavItemVariants>;

export type MobileNavSeparatorProps = Omit<React.ComponentProps<'div'>, 'style'>;

export function MobileNavSeparator({ className, ...props }: MobileNavSeparatorProps) {
  return (
    <div
      data-slot='mobile-nav-separator'
      role='separator'
      className={cn('mx-3.5 my-2.5 h-px bg-line', className)}
      {...props}
    />
  );
}
